import { useState, useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import { getDayIndexFromPosition, weekdayOrdinal, calendarDayFromWeekdayOrdinal } from '../utils/calendarPositioning';
import { haptic } from '../../../utils/haptics';

export type DragMode = 'create' | 'move' | 'resize';

export interface DragState {
    mode: DragMode;
    startDay: number;
    currentDay: number;
    clickOffset?: number;
    userId?: string;
    itemId?: string;
    originalStartDay?: number;
    originalEndDay?: number;
    draggedStatus?: string;
    originalWeekdays?: number;
    resizeEdge?: 'start' | 'end';
}

export interface DragCallbacks {
    onDragCreate?: (userId: string, startDate: Date, endDate: Date) => void;
    onDragMove?: (itemId: string, startDate: Date, endDate: Date) => void;
    onDragResize?: (itemId: string, startDate: Date, endDate: Date) => void;
    onDragHighlight?: (highlight: { startDayIndex: number; endDayIndex: number } | null) => void;
}

export const useDragState = (
    startDate: moment.Moment,
    length: number,
    callbacks: DragCallbacks
) => {
    const [dragState, setDragState] = useState<DragState | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);

    const isDragging = dragState !== null;

    const startDrag = useCallback((
        mode: DragMode,
        initialX: number,
        options?: {
            userId?: string;
            itemId?: string;
            originalStartDay?: number;
            originalEndDay?: number;
            draggedStatus?: string;
            originalWeekdays?: number;
            resizeEdge?: 'start' | 'end';
        }
    ) => {
        const dayIndex = getDayIndexFromPosition(initialX, containerRef, startDate, length);

        // For move mode, calculate the click offset within the original forecast
        let clickOffset = 0;
        if (mode === 'move' && options?.originalStartDay !== undefined) {
            clickOffset = dayIndex - options.originalStartDay;
        }

        const newDragState: DragState = {
            mode,
            startDay: dayIndex,
            currentDay: dayIndex,
            clickOffset,
            ...options
        };

        isDraggingRef.current = true;
        setDragState(newDragState);

        // Initial highlight
        if (callbacks.onDragHighlight) {
            const highlightStart = mode === 'create' ? dayIndex : (options?.originalStartDay ?? dayIndex);
            const highlightEnd = mode === 'create' ? dayIndex : (options?.originalEndDay ?? dayIndex);
            callbacks.onDragHighlight({ startDayIndex: highlightStart, endDayIndex: highlightEnd });
        }
    }, [startDate, length, callbacks.onDragHighlight]);

    const updateDrag = useCallback((x: number) => {
        if (!dragState) return;

        const dayIndex = getDayIndexFromPosition(x, containerRef, startDate, length);

        // Only update if day actually changed
        if (dayIndex !== dragState.currentDay) {
            haptic('selection');
            const updatedDragState = { ...dragState, currentDay: dayIndex };
            setDragState(updatedDragState);

            // Update highlight based on drag mode
            if (callbacks.onDragHighlight) {
                let highlightStart: number;
                let highlightEnd: number;

                switch (dragState.mode) {
                    case 'create':
                        highlightStart = Math.min(dragState.startDay, dayIndex);
                        highlightEnd = Math.max(dragState.startDay, dayIndex);
                        break;
                    case 'move': {
                        // Calculate movement in weekdays to avoid jumps when crossing weekends
                        const cursorWeekdayDelta = weekdayOrdinal(startDate, dayIndex) - weekdayOrdinal(startDate, dragState.startDay);
                        const origStartOrd = weekdayOrdinal(startDate, dragState.originalStartDay ?? 0);
                        const newStartOrd = origStartOrd + cursorWeekdayDelta;
                        const moveNewStartDay = calendarDayFromWeekdayOrdinal(startDate, newStartOrd);
                        if (dragState.originalWeekdays) {
                            highlightStart = moveNewStartDay;
                            highlightEnd = calendarDayFromWeekdayOrdinal(startDate, newStartOrd + dragState.originalWeekdays - 1);
                        } else {
                            const forecastLength = (dragState.originalEndDay ?? 0) - (dragState.originalStartDay ?? 0);
                            highlightStart = moveNewStartDay;
                            highlightEnd = moveNewStartDay + forecastLength;
                        }
                        break;
                    }
                    case 'resize':
                        // For resize, determine which edge is being resized
                        if (dragState.resizeEdge === 'start') {
                            highlightStart = dayIndex;
                            highlightEnd = dragState.originalEndDay ?? 0;
                        } else {
                            highlightStart = dragState.originalStartDay ?? 0;
                            highlightEnd = dayIndex;
                        }
                        break;
                }

                callbacks.onDragHighlight({ startDayIndex: highlightStart, endDayIndex: highlightEnd });
            }
        }
    }, [dragState, startDate, length, callbacks.onDragHighlight]);

    const endDrag = useCallback(() => {
        if (!dragState) return;

        const { mode, startDay, currentDay, userId, itemId, originalStartDay, originalEndDay } = dragState;

        // Calculate final dates based on drag mode
        let finalStartDay: number;
        let finalEndDay: number;

        switch (mode) {
            case 'create':
                finalStartDay = Math.min(startDay, currentDay);
                finalEndDay = Math.max(startDay, currentDay);
                break;
            case 'move': {
                // Calculate movement in weekdays to avoid jumps when crossing weekends
                const cursorWeekdayDelta = weekdayOrdinal(startDate, currentDay) - weekdayOrdinal(startDate, startDay);
                const origStartOrd = weekdayOrdinal(startDate, originalStartDay ?? 0);
                const newStartOrd = origStartOrd + cursorWeekdayDelta;
                finalStartDay = calendarDayFromWeekdayOrdinal(startDate, newStartOrd);
                if (dragState.originalWeekdays) {
                    finalEndDay = calendarDayFromWeekdayOrdinal(startDate, newStartOrd + dragState.originalWeekdays - 1);
                } else {
                    const forecastLength = (originalEndDay ?? 0) - (originalStartDay ?? 0);
                    finalEndDay = finalStartDay + forecastLength;
                }
                break;
            }
            case 'resize':
                if (dragState.resizeEdge === 'start') {
                    finalStartDay = currentDay;
                    finalEndDay = originalEndDay ?? 0;
                } else {
                    finalStartDay = originalStartDay ?? 0;
                    finalEndDay = currentDay;
                }
                break;
        }

        const finalStartDate = moment(startDate).add(finalStartDay, 'days').toDate();
        const finalEndDate = moment(startDate).add(finalEndDay, 'days').toDate();

        // Call appropriate callback
        switch (mode) {
            case 'create':
                if (callbacks.onDragCreate && userId) {
                    callbacks.onDragCreate(userId, finalStartDate, finalEndDate);
                }
                break;
            case 'move':
                if (callbacks.onDragMove && itemId) {
                    callbacks.onDragMove(itemId, finalStartDate, finalEndDate);
                }
                break;
            case 'resize':
                if (callbacks.onDragResize && itemId) {
                    callbacks.onDragResize(itemId, finalStartDate, finalEndDate);
                }
                break;
        }

        // Clear drag state and highlight
        isDraggingRef.current = false;
        setDragState(null);
        callbacks.onDragHighlight?.(null);
    }, [dragState, startDate, callbacks]);

    const cancelDrag = useCallback(() => {
        isDraggingRef.current = false;
        setDragState(null);
        callbacks.onDragHighlight?.(null);
    }, [callbacks.onDragHighlight]);

    // Global mouse event handlers
    const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            updateDrag(e.clientX);
        }
    }, [isDragging, updateDrag]);

    const handleGlobalMouseUp = useCallback(() => {
        if (isDragging) {
            endDrag();
        }
    }, [isDragging, endDrag]);

    // Global touch event handlers
    const handleGlobalTouchMove = useCallback((e: TouchEvent) => {
        if (isDragging) {
            e.preventDefault();
            updateDrag(e.touches[0].clientX);
        }
    }, [isDragging, updateDrag]);

    const handleGlobalTouchEnd = useCallback(() => {
        if (isDragging) {
            endDrag();
        }
    }, [isDragging, endDrag]);

    // Always-registered touchmove listener to prevent scroll during any drag.
    // Uses ref for immediate synchronous check (no React render delay).
    useEffect(() => {
        const preventScrollDuringDrag = (e: TouchEvent) => {
            if (isDraggingRef.current) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', preventScrollDuringDrag, { passive: false });
        return () => {
            document.removeEventListener('touchmove', preventScrollDuringDrag);
        };
    }, []);

    // Set up global event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
            document.addEventListener('touchend', handleGlobalTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('touchmove', handleGlobalTouchMove);
            document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
    }, [isDragging, handleGlobalMouseMove, handleGlobalMouseUp, handleGlobalTouchMove, handleGlobalTouchEnd]);

    return {
        dragState,
        isDragging,
        containerRef,
        startDrag,
        updateDrag,
        endDrag,
        cancelDrag
    };
}; 